import React, { useEffect, useState } from "react";
import {
  Button,
  DialogTitle,
  Divider,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";
import CustomDialog from "components/reusables/CustomDialog";
import { getUserData, getItem, setItem, showToast } from "services/utils";
import withRouter from "../withRouter";
import { Constants } from "services/keyConstants";

// Subcomponent for countdown display
const ESignCountdown = ({ lastSignTime }) => {
  const [countdown, setCountdown] = useState("");

  useEffect(() => {
    if (!lastSignTime) return;

    const updateCountdown = () => {
      const last = new Date(lastSignTime).getTime();
      const now = Date.now();
      const diffMs = 30 * 60 * 1000 - (now - last);

      if (diffMs <= 0) {
        setCountdown("");
        return;
      }

      const hours = Math.floor(diffMs / 3600000);
      const minutes = Math.floor((diffMs % 3600000) / 60000);
      const seconds = Math.floor((diffMs % 60000) / 1000);

      setCountdown(
        `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
          2,
          "0"
        )}:${String(seconds).padStart(2, "0")}`
      );
    };

    updateCountdown(); // Set initial
    const timer = setInterval(updateCountdown, 1000);

    return () => clearInterval(timer);
  }, [lastSignTime]);

  if (!countdown) return null;

  return (
    <span style={{ marginLeft: 8, fontWeight: "normal" }}>
      (eSign session ends in {countdown})
    </span>
  );
};

const CustomESign = (props) => {
  const {
    open,
    setOpen,
    handleSubmit,
    userData,
    buttonName = "E-Sign",
    esignType = "",
    color = "",
    disabled = false,
  } = props;

  const defaultPassword = window.location.href.includes(
    "me.mavenregulatory.com"
  )
    ? "12345"
    : "";

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const lastSignTime = getItem(Constants.ESIGNTIME);
  let within30Min = false;

  if (lastSignTime) {
    const lastTime = new Date(lastSignTime).getTime();
    const now = Date.now();
    const diffMinutes = (now - lastTime) / 1000 / 60;
    within30Min = diffMinutes <= 30;
  }

  const handleModalClose = () => {
    setOpen(false);
    formik.resetForm();
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const formik = useFormik({
    initialValues: {
      password: defaultPassword,
      confirmPassword: defaultPassword,
    },
    validationSchema: Yup.object({
      password: Yup.string().required("Password is required"),
      ...(within30Min
        ? {}
        : {
            confirmPassword: Yup.string()
              .oneOf([Yup.ref("password")], "Passwords must match")
              .required("Confirm Password is required"),
          }),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      const reqObj = {
        password: values?.password,
      };

      const uri = props?.commonUrl?.E_SIGN;
      const result = await props?.ApiServices.callServicePostWithBodyData(
        uri,
        reqObj
      );

      if (result?.meta?.success) {
        const value = {
          time: new Date().toISOString(),
          userData: getUserData(userData),
        };
        setItem(Constants.ESIGNTIME, value?.time);
        showToast("ESign Added Successfully", true);
        handleSubmit(value, esignType);
        handleModalClose();
      } else {
        showToast(result);
      }

      setSubmitting(false);
    },
  });

  return (
    <>
      <Button
        variant="contained"
        onClick={() => setOpen(true)}
        style={{ marginLeft: "10px" }}
        disabled={disabled}
      >
        {buttonName}
      </Button>

      <CustomDialog open={open} onCancel={handleModalClose} maxWidth="sm">
        <DialogTitle>
          Enter Password To E-Sign
          {lastSignTime && (
            <>
              <ESignCountdown lastSignTime={lastSignTime} />
            </>
          )}
        </DialogTitle>
        <Divider />

        <form onSubmit={formik.handleSubmit}>
          <DialogContent>
            <TextField
              fullWidth
              label="Password"
              name="password"
              type={showPassword ? "text" : "password"}
              value={formik.values.password}
              onChange={formik.handleChange}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
              sx={{ mb: 2 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((prev) => !prev)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {!within30Min && (
              <TextField
                fullWidth
                label="Confirm Password"
                name="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                error={
                  formik.touched.confirmPassword &&
                  Boolean(formik.errors.confirmPassword)
                }
                helperText={
                  formik.touched.confirmPassword &&
                  formik.errors.confirmPassword
                }
                sx={{ mb: 2 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                        edge="end"
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          </DialogContent>

          <Divider />
          <DialogActions sx={{ p: 2.5 }}>
            <Button
              variant="contained"
              color="error"
              onClick={handleModalClose}
            >
              Close
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="success"
              disabled={formik.isSubmitting}
            >
              Submit
            </Button>
          </DialogActions>
        </form>
      </CustomDialog>
    </>
  );
};

export default withRouter(CustomESign);
